// framework/client/core/ComponentManager.js

import { ComponentBase } from "./ComponentBase.js";

export class ComponentManager extends ComponentBase {
    constructor(layoutEngine) {
        super({ appClient: layoutEngine.appClient });
        this.layoutEngine = layoutEngine;
        this.componentRegistry = {};
        this.componentInstances = {};

        this.managedComponents = new Map();
        this.appClient.events.server.on('reloadComponent', this.handleReloadManagedComponent.bind(this));
    }

    registerComponent(type, componentClass) {
        this.componentRegistry[type] = componentClass;
    }

    async getOrCreateComponent(config, useCache = true) {
        const instanceId = config.instanceId || this.generateInstanceId(config);

        this.log.info(`Creating new instance for component: ${config.name || config.type} (${instanceId})`);
        const ComponentClass = await this.loadComponent(config.type, useCache);
        if (!ComponentClass) {
            this.log.error(`Component type not found: ${config.type}`);
            return null;
        }

        const componentInstance = new ComponentClass({
            appClient: this.appClient,
            ...config.props,
            instanceId
        }); 

        await componentInstance.initialize();

        // Cache the instance only after successful initialization
        this.componentInstances[instanceId] = componentInstance;
        return componentInstance;
    }

    async loadComponent(type, useCache = true) {
        try {
            // NEW: Use the /components/ route for loading
            const modulePath = `/components/${type}.js`;
            const module = await import(modulePath + (useCache ? '' : `?t=${Date.now()}`)); // Cache busting
            
            // --- FIX START ---
            // If the type contains slashes (e.g. 'agent-config/AgentConfigIdentity'), 
            // we need to extract just the class name (e.g. 'AgentConfigIdentity') 
            // to find it in the module exports.
            const className = type.split('/').pop();
            return module[className] || module.default;
            // --- FIX END ---

        } catch (error) {
            this.log.error(`Error loading component ${type}:`, error);
            return null;
        }
    }

    async createManagedComponent(type, parent, config, useCache = true) {
        if (!config.type) {
            config.type = type;
        }
        // NEW: If no "props" is defined (common for nested components), nest all non-reserved keys into config.props.
        if (config.props === undefined) {
            const reserved = new Set(["name", "type", "container", "instanceId", "props", "appClient"]);
            config.props = {};
            for (const key in config) {
                if (!reserved.has(key)) {
                    config.props[key] = config[key];
                    delete config[key];
                }
            }
        }

        // Inject appClient into the config.
        config.appClient = this.appClient;

        // Set or generate instanceId.
        const instanceId = config.instanceId || this.generateInstanceId(config);
        config.instanceId = instanceId;

        // Create the component instance (using cache by default).
        const componentInstance = await this.getOrCreateComponent(config,useCache);
        const element = await componentInstance.render();

        // Resolve parent element (accept both selectors or HTML elements).
        let parentElement;
        if (typeof parent === "string") {
            parentElement = document.querySelector(parent);
        } else {
            parentElement = parent;
        }
        if (parentElement) {
            parentElement.appendChild(element);
        }

        // Save in managed components map.
        this.managedComponents.set(instanceId, {
            config,
            instance: componentInstance,
            element,
        });

        // Return a Proxy that always delegates to the latest instance.
        const proxy = new Proxy({}, {
            get: (target, prop) => {
                const managed = this.managedComponents.get(instanceId);
                if (!managed) {
                    this.log.warn(`Managed component ${instanceId} not found.`);
                    return undefined;
                }
                const value = managed.instance[prop];

                if (prop === "destroy" && typeof value === "function") {
                    return async (...args) => {
                        await value.apply(managed.instance, args); // Call original destroy
                        this.managedComponents.delete(instanceId); // Remove from map
                        delete this.componentInstances[instanceId]; 
                    };
                }

                return typeof value === "function" ? value.bind(managed.instance) : value;
            },
            set: (target, prop, value) => {
                const managed = this.managedComponents.get(instanceId);
                if (managed) {
                    managed.instance[prop] = value;
                    return true;
                }
                return false;
            }
        });
        return proxy;
    }


    async handleReloadManagedComponent({ name }) {
        this.log.info(`Hot reloading managed components of type: ${name}`);
        let styleRemoved = false; // flag to remove styles only once per component type
        // Iterate over all managed components to find those matching the changed type.
        for (const [instanceId, managed] of this.managedComponents.entries()) {
            // Check if the managed component type matches the reloaded name
            // We check both exact match and if the type ends with the name (for subfolders)
            if (managed.config.type !== name && !managed.config.type.endsWith(`/${name}`)) continue;
            
            try {
                const parent = managed.element.parentNode;
                const state = managed.instance.getState();
                if (managed.instance && typeof managed.instance.destroy === "function") {
                    if (!styleRemoved && typeof managed.instance.removeStyles === "function") {
                        managed.instance.removeStyles();
                        styleRemoved = true;
                    }
                    await managed.instance.destroy();
                }
                // Create a new instance bypassing the cache.
                const newComponent = await this.getOrCreateComponent(managed.config, false);
                const newElement = await newComponent.render();
                newComponent.restoreState(state);
                if (parent && parent.contains(managed.element)) {
                    parent.replaceChild(newElement, managed.element);
                } else if (parent) {
                    parent.appendChild(newElement);
                    this.log.warn(`Managed component ${instanceId} was removed; appended new one instead.`);
                } else {
                    this.log.warn(`Parent not found for managed component ${instanceId}`);
                }
                // Update the managed component record.
                managed.instance = newComponent;
                managed.element = newElement;
                this.log.info(`Managed component ${instanceId} reloaded successfully.`);
            } catch (error) {
                this.log.error(`Error reloading managed component ${instanceId}:`, error);
            }
        }
    }


    generateInstanceId(config) {
        // Generate a unique ID based on component type and other relevant properties
        return `${config.type}-${Math.random().toString(36).substr(2, 9)}`;
    }

    arePropsEqual(props1, props2) {
        // Basic comparison, you might need a more sophisticated check based on your props structure
        return JSON.stringify(props1) === JSON.stringify(props2);
    }

}